import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Clock, Loader2 } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useMyDualRoleRequest, useRequestDualRole } from "@/hooks/useDualRoleRequest";
import { useCancelDualRoleRequest } from "@/hooks/useDualRoleRequestsAdmin";

const ROLE_LABEL: Record<"mentor" | "entrepreneur", string> = {
  mentor: "Mentor",
  entrepreneur: "Entrepreneur",
};

export function DualRoleRequestSection() {
  const { data: profile } = useProfile();
  const { data: request, isLoading } = useMyDualRoleRequest();
  const requestMutation = useRequestDualRole();
  const cancel = useCancelDualRoleRequest();

  if (!profile) return null;

  const hasMentor = (profile as any).is_mentor as boolean | undefined;
  const hasEntrepreneur = (profile as any).is_entrepreneur as boolean | undefined;

  // Already has both capabilities — nothing to show
  if (hasMentor && hasEntrepreneur) return null;

  // Determine which role they don't have yet
  const missingRole: "mentor" | "entrepreneur" | null = !hasMentor
    ? "mentor"
    : !hasEntrepreneur
      ? "entrepreneur"
      : null;

  if (!missingRole) return null;

  const pendingForMissing =
    request?.status === "pending" && request.requested_role === missingRole;
  const declinedRecent =
    request?.status === "declined" && request.requested_role === missingRole;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Add {ROLE_LABEL[missingRole]} Capability
        </CardTitle>
        <CardDescription>
          Request approval to also participate as a {ROLE_LABEL[missingRole].toLowerCase()} on the platform.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : pendingForMissing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-warning text-warning">
                <Clock className="h-3 w-3 mr-1" />
                Pending review
              </Badge>
              <span className="text-sm text-muted-foreground">
                Submitted {new Date(request!.requested_at).toLocaleDateString()}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Your request to add the {request!.requested_role} role is pending admin review.
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate(request!.id)}
            >
              {cancel.isPending ? "Withdrawing…" : "Withdraw Request"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {declinedRecent && (
              <p className="text-sm text-muted-foreground">
                Your previous request was not approved. You can submit a new request below.
              </p>
            )}
            <Button
              onClick={() => requestMutation.mutate(missingRole)}
              disabled={requestMutation.isPending}
            >
              {requestMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Request {ROLE_LABEL[missingRole]} Role
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
